// libs
import { createSearchParams, useNavigate, useParams, useSearchParams } from "react-router-dom";
import React, { createContext, useContext, useCallback, useReducer, useEffect, useRef, useState } from "react";

// defs
import { ISimulationInput } from "@defs/Utilities";
import { IStepper } from "@components/Stepper/Stepper.props";
import { AllocationType, InputType, Scenario, Sim, SimStatus } from "@utils/appsync/schema/API";
import { INewSimulationState, getInitialNewSimulationState } from "@store/state/NewSimulation";
import { SEVERITY } from "@defs/Severity";

// hooks
import { useFeatureFlags } from "@hooks/useFeatureFlags";
import { useAlertMessageHandler } from "@hooks/useErrorMessageHandler";

// constants
import { FeatureFlag } from "@constants/FeatureFlags";
import { GLSimulationSteppersType } from "@pages/simulations/NewSimulation/containers/GLSimulation/constants";
import { OpexAllocationSteppersType } from "@pages/simulations/NewSimulation/containers/OpexAllocation/constants";

// store
import {
    setReadOnly,
    NewSimulationActionType,
    setSelectedScenario,
    setSimulationData,
    setSimulationId,
    setActiveSimulationStepper,
    setSimulationSteppers,
    setSimulationInputs,
    setSelectedCostPoolList,
    setIsSystemDefinitionAndMappingSelected,
} from "@store/actions/NewSimulation";
import { AppContext } from "@store/context/App";
import { UserInfoContext } from "@store/context/UserInfo";
import { newSimulationReducer } from "@store/reducer/NewSimulation";

// services
import { useGetScenariosList, useGetSimulation, useSaveSimulation } from "@services/SimulationService";
import { rumService } from "@services/rumService";
import { RUM_EVENT_TYPES, RUM_ACTIONS, RUM_COMPONENTS } from "@services/rumEventHelpers";

// helpers
import { debounce } from "@helpers/index";
import { initializeNewSimulationState, updateNewSimulationState } from "@store/helpers/NewSimulation";

export interface INewSimulationContext extends INewSimulationState {
    setReadOnly: (readOnly: boolean) => void;
    isSimulationHeaderEnabled: (
        simulationSteppers: Array<IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>>,
        activeSimulationStepper: IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>,
    ) => boolean;
    isSaveEnabled: (simulationData: Partial<Sim>) => boolean;
    isStepperEnabled: (simulationData: Partial<Sim>) => boolean;
    isNextButtonEnabled: (simulationData: Partial<Sim>) => boolean;
    isStartRunEnabled: (simulationData: Partial<Sim>) => boolean;
    setSimulationId: (simulationId: string) => void;
    setSimulationData: (simulationData: Sim) => void;
    setSelectedScenario: (selectedScenario: Scenario) => void;
    setSelectedCostPoolList: (selectedCostPoolList: Array<string>) => void;
    setSimulationSteppers: (simulationSteppers: Array<IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>>) => void;
    setActiveSimulationStepper: (activeSimulationStepper: IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>) => void;
    handleStepChange: (newStep: any) => void;
    setSimulationInputs: (simulationInputs: Array<ISimulationInput<InputType>>) => void;
    getSelectedInput: (fieldName: string) => string;
    updateSimulationData: (fieldName: string, maxPeriod?: string) => (fieldValue: any) => void;
    setIsSystemDefinitionAndMappingSelected: (isSystemDefinitionAndMappingSelected: boolean) => void;
    isSaving: boolean;
    isDebouncing: boolean;
    isLoadingSimulation: boolean;
    autoSaveMode: boolean;
}

export const NewSimulationContext = createContext<INewSimulationContext | undefined>(undefined);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const NewSimulationProvider = ({ children }): any => {
    const navigate = useNavigate();

    const { resourceKey } = useParams();

    const [searchParams] = useSearchParams();

    const { setPageTitle } = useContext(AppContext);

    const { username, selectedSandbox, selectedAllocationType } = useContext(UserInfoContext);

    const [state, dispatch] = useReducer(newSimulationReducer, getInitialNewSimulationState(selectedAllocationType.key));

    const saveSimulation = useSaveSimulation();

    const { isFeatureEnable } = useFeatureFlags(username, true);

    const { showErrorAlertMessage } = useAlertMessageHandler();

    // React Query caches scenarios per allocation type automatically via queryKey
    const { data: scenarioList } = useGetScenariosList(selectedAllocationType?.key, true, true, true);

    // Track if we've already navigated to a new URL for this simulation
    // This helps prevent duplicate simulations from being created
    const hasNavigatedRef = useRef(false);

    // Track the current simulation ID to prevent duplicate saves
    const currentSimIdRef = useRef<string | null>(null);

    // Track if a save operation is currently in progress
    const isSavingRef = useRef(false);

    // Track the last saved simulation data to prevent unnecessary saves
    const [lastSavedData, setLastSavedData] = useState<string | null>(null);

    // Track saving state for UI loading indicators
    const [isSaving, setIsSaving] = useState(false);

    // Get simId from query parameter (Step 1) or path parameter (Step 2+)
    // Support both plain resource_id and base64
    const getSimIdFromURL = (): string => {
        const simIdFromQuery = searchParams.get("simId");
        const simIdFromPath = resourceKey;

        const urlParam = simIdFromQuery || simIdFromPath;
        if (!urlParam) return "";

        // Detect if it's plain resource_id (starts with "01") or base64
        if (urlParam.startsWith("01")) {
            return urlParam;
        } else {
            return atob(urlParam);
        }
    };

    const simIdFromURL = getSimIdFromURL();
    const {
        data: simulationData,
        isFetching: isLoadingSimulation,
        refetch: refetchSimulation,
    } = useGetSimulation(
        selectedSandbox.sandbox_id,
        simIdFromURL,
        false,
        !!simIdFromURL, // Only enable query if simId exists
    );

    // Track debouncing state
    const [isDebouncing, setIsDebouncing] = useState(false);

    // Track debouncing state in ref for stable callback access
    const isDebouncingRef = useRef(false);

    // Track latest simulation data for debounced saves
    const latestDataRef = useRef<any>(null);

    // Sync ref with state
    useEffect(() => {
        isDebouncingRef.current = isDebouncing;
    }, [isDebouncing]);

    /*
     * Initializes the new simulation state on component mount or when `selectedSandbox` or `allocationType` or `resourceKey` changes.
     *
     * - Sets the page title to "New Simulation".
     * - If a sandbox is selected:
     *   - Decodes the resource key (if present) and sets the simulation ID.
     *   - Initializes the new simulation state based on the selected allocation type.
     *
     * This setup prepares the simulation context properly when navigating to the New Simulation screen.
     */
    useEffect(() => {
        // set the page title
        setPageTitle({
            title: "New Simulation",
        });

        if (selectedSandbox?.sandbox_id && selectedAllocationType) {
            const simId = getSimIdFromURL();
            if (simId) {
                // Existing simulation - set ID and mark as navigated
                dispatch({ type: NewSimulationActionType.SET_SIMULATION_ID, payload: { simulationId: simId } });
                currentSimIdRef.current = simId;
                hasNavigatedRef.current = true;
            } else {
                // NEW simulation - clear all refs and initialize empty state
                currentSimIdRef.current = null;
                hasNavigatedRef.current = false;
                setLastSavedData(null);
                latestDataRef.current = null; // Clear pending changes
                setIsDebouncing(false); // Clear debouncing state
                dispatch({
                    type: NewSimulationActionType.INITIALIZE_NEW_SIMULATION_STATE,
                    payload: {
                        ...initializeNewSimulationState(selectedAllocationType),
                        simulationData: null, // Explicitly clear simulation data
                        simulationId: null, // Explicitly clear simulation ID
                    },
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSandbox, selectedAllocationType, resourceKey, simIdFromURL, searchParams]);

    /*
     * update the new simulation state on change of simulation data
     * - Sets the page title to "New Simulation" or "Simulaton" based upon simulation status
     * - Update the stepper state, selected scenario etc
     */
    useEffect(() => {
        // set the page title based upon simulation status
        setPageTitle({
            title: !simulationData || simulationData?.status === SimStatus.CREATED ? "New Simulation" : "Simulation",
        });

        // Skip restoration if there are pending unsaved changes to prevent overwriting local edits
        const hasPendingChanges = latestDataRef.current !== null || isDebouncing || isSaving;
        if (hasPendingChanges) {
            return;
        }

        // Only update state if we have simulationData AND a simId in the URL
        // AND the simulationData matches the simId from URL
        if (simulationData && simIdFromURL && simulationData.resource_id === simIdFromURL) {
            // Store the current simulation ID
            currentSimIdRef.current = simulationData.resource_id;

            // Always update last saved data for tracking
            setLastSavedData(
                JSON.stringify({
                    name: simulationData.name,
                    configs: simulationData.configs,
                    resource_id: simulationData.resource_id,
                }),
            );

            // Restore step from URL using step by name
            const stepFromURL = searchParams.get("step");

            // Update the simulation context/state based on updated simulation data
            const updatedState = updateNewSimulationState(state, selectedAllocationType, simulationData, scenarioList);

            // Override the activeSimulationStepper if we have a step name in URL
            if (stepFromURL && updatedState.simulationSteppers) {
                const targetStepIndex = updatedState.simulationSteppers.findIndex((s) => s.type === stepFromURL);

                if (targetStepIndex >= 0) {
                    // Mark all previous steps as complete
                    updatedState.simulationSteppers = updatedState.simulationSteppers.map((stepper, index) => ({
                        ...stepper,
                        isComplete: index < targetStepIndex,
                        isActive: index === targetStepIndex,
                    }));
                    updatedState.activeSimulationStepper = updatedState.simulationSteppers[targetStepIndex];
                }
            }

            dispatch({
                type: NewSimulationActionType.UPDATE_NEW_SIMULATION_STATE,
                payload: {
                    ...updatedState,
                    simulationId: state.simulationId || simulationData.resource_id,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [simulationData, scenarioList, searchParams]);

    const isSimulationHeaderEnabled = (
        simulationSteppers: Array<IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>>,
        activeSimulationStepper: IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>,
    ): boolean => {
        // Enable or allow editing of Simulation Header (Simulation Name, Scenario and time periods input) only when
        // user is on the first stepper. Hide if user is in the middle of any other step.
        return simulationSteppers[0].type === activeSimulationStepper.type;
    };

    const isSaveEnabled = (simulationData: Partial<Sim>): boolean => {
        const simulationDataConfig = JSON.parse(simulationData?.configs ?? "{}");

        return (
            !isFeatureEnable(FeatureFlag.SANDBOX_GLOBAL_LOCK) &&
            !!simulationData?.name &&
            !!simulationDataConfig?.scenario &&
            !!simulationDataConfig?.startPeriod &&
            !!simulationDataConfig?.endPeriod
        );
    };

    const isStepperEnabled = (simulationData: Partial<Sim>): boolean => {
        const simulationDataConfig = JSON.parse(simulationData?.configs ?? "{}");

        return (
            !!simulationData?.name &&
            !!simulationDataConfig?.scenario &&
            !!simulationDataConfig?.startPeriod &&
            !!simulationDataConfig?.endPeriod
        );
    };

    const isNextButtonEnabled = (simulationData: Partial<Sim>): boolean => {
        // Check if there are any save operation failures
        const hasSaveError = saveSimulation.isError;

        // Disable Next button if save is in progress (debounce or API call)
        const isSaveInProgress = isDebouncing || isSaving;

        // Next button should be enabled only if stepper is enabled AND there are no save errors AND no save in progress
        return isStepperEnabled(simulationData) && !hasSaveError && !isSaveInProgress;
    };

    const isStartRunEnabled = (simulationData: Partial<Sim>): boolean => {
        const simulationDataConfig = JSON.parse(simulationData?.configs ?? "{}");

        switch (selectedAllocationType?.key) {
            case AllocationType.opex_allocation:
                return (
                    isSaveEnabled &&
                    state.activeSimulationStepper?.type === OpexAllocationSteppersType.REVIEW_AND_RUN &&
                    (!!simulationDataConfig?.allocation_mapping || !!simulationDataConfig?.allocation_definition)
                );

            case AllocationType.gl_simulation:
                if (state.isSystemDefinitionAndMappingSelected) return isSaveEnabled && !!simulationDataConfig?.manual_journal_entry;

                return (
                    isSaveEnabled &&
                    !!simulationDataConfig?.manual_journal_entry &&
                    (!!simulationDataConfig?.allocation_mapping || !!simulationDataConfig?.allocation_definition)
                );
        }
    };

    // Function to check if the data has changed since the last save
    const hasDataChanged = useCallback(
        (updatedSimulationData: any) => {
            // Null check - if no data provided, consider unchanged
            if (!updatedSimulationData) {
                return false;
            }
            if (!lastSavedData) return true;

            // Parse and normalize configs to ensure consistent comparison
            const normalizeConfigs = (configs: string): string => {
                if (!configs) return "{}";

                if (typeof configs !== "string") {
                    return JSON.stringify(configs);
                }

                try {
                    // Parse then re-stringify to normalize JSON formatting (removes whitespace/ordering differences)
                    return JSON.stringify(JSON.parse(configs));
                } catch (error) {
                    console.error("Invalid JSON in configs", configs, error);
                    return "{}";
                }
            };

            const currentData = JSON.stringify({
                name: updatedSimulationData.name,
                configs: normalizeConfigs(updatedSimulationData.configs),
                resource_id: updatedSimulationData.resource_id,
            });

            const lastSaved = JSON.parse(lastSavedData);
            const savedData = JSON.stringify({
                name: lastSaved.name,
                configs: normalizeConfigs(lastSaved.configs),
                resource_id: lastSaved.resource_id,
            });

            return currentData !== savedData;
        },
        [lastSavedData],
    );

    // Create a single save function that will be used by the debounced function
    const saveSimulationData = useCallback(
        async (updatedSimulationData: any) => {
            // If a save is already in progress, don't start another one
            if (isSavingRef.current) {
                return;
            }
            // If we already have a simulation ID in the URL or state, use it
            const simId = updatedSimulationData.resource_id || currentSimIdRef.current;

            // Mark that a save is in progress
            isSavingRef.current = true;
            setIsSaving(true);

            try {
                const savedSimulationData = await saveSimulation.mutateAsync({
                    sandbox_id: selectedSandbox.sandbox_id,
                    sim_id: simId,
                    request: {
                        name: updatedSimulationData.name,
                        configs: updatedSimulationData.configs,
                        allocation_type: selectedAllocationType.key,
                        userAlias: username,
                    },
                });
                currentSimIdRef.current = savedSimulationData.resource_id;

                const isNewSimCreation = !resourceKey?.length && !searchParams.get("simId") && !hasNavigatedRef.current;
                if (isNewSimCreation) {
                    setLastSavedData(
                        JSON.stringify({
                            name: savedSimulationData.name,
                            configs: savedSimulationData.configs,
                            resource_id: savedSimulationData.resource_id,
                        }),
                    );

                    // Check if user made changes while CreateSim was running
                    const hasPendingChanges = latestDataRef.current !== null;

                    if (!hasPendingChanges) {
                        // Safe to dispatch - no pending changes to overwrite
                        dispatch({
                            type: NewSimulationActionType.SET_SIMULATION_DATA,
                            payload: { simulationData: savedSimulationData, simulationId: savedSimulationData.resource_id },
                        });
                    }
                    // If pending changes exist, skip dispatch - let debounced save handle it

                    hasNavigatedRef.current = true;

                    rumService.recordBusinessEvent(
                        RUM_EVENT_TYPES.SIMULATION_CREATED,
                        RUM_ACTIONS.CREATE_SIM,
                        RUM_COMPONENTS.SIMULATION,
                        username,
                        {
                            simulationId: savedSimulationData.resource_id,
                            simulationName: savedSimulationData.name,
                            sandboxId: selectedSandbox.sandbox_id,
                            allocationType: selectedAllocationType.key,
                        },
                    );

                    // Use query parameter for Step 1 (no remount, smooth UX)
                    navigate(
                        {
                            pathname: "/simulation",
                            search: createSearchParams({
                                sandboxId: selectedSandbox.sandbox_id,
                                allocationType: selectedAllocationType.key,
                                simId: savedSimulationData.resource_id,
                            }).toString(),
                        },
                        { replace: true },
                    );
                } else {
                    // For EXISTING sim updates: Use conditional dispatch (debounce logic)
                    const shouldSkipDispatch =
                        isDebouncingRef.current && latestDataRef.current && latestDataRef.current !== updatedSimulationData;
                    if (!shouldSkipDispatch) {
                        setLastSavedData(
                            JSON.stringify({
                                name: savedSimulationData.name,
                                configs: savedSimulationData.configs,
                                resource_id: savedSimulationData.resource_id,
                            }),
                        );
                        dispatch({
                            type: NewSimulationActionType.SET_SIMULATION_DATA,
                            payload: { simulationData: savedSimulationData, simulationId: savedSimulationData.resource_id },
                        });
                    }

                    // Refetch simulation data to update React Query cache with latest inputId
                    refetchSimulation();
                }
            } catch (error: any) {
                // Handle errors thrown from service layer (always the GraphQL errors array)
                const customErrorMessage = {
                    title: "Operation Failed",
                    content: error?.errors?.[0]?.message || "Operation failed",
                    severity: SEVERITY.ERROR,
                    autoTimeout: 6000,
                };
                showErrorAlertMessage(error, customErrorMessage);
            } finally {
                // Mark that the save is complete
                isSavingRef.current = false;
                setIsSaving(false);

                // Check if latestDataRef has different data than what was just saved
                if (latestDataRef.current && latestDataRef.current !== updatedSimulationData) {
                    const dataToSave = latestDataRef.current;
                    setTimeout(() => {
                        latestDataRef.current = null; // Clear ref to prevent double save
                        saveSimulationData(dataToSave);
                    }, 100);
                } else {
                    // No more data to save - clear debouncing state
                    if (latestDataRef.current) {
                        latestDataRef.current = null;
                    }
                    setIsDebouncing(false);
                }
            }
        },
        [
            saveSimulation,
            selectedSandbox.sandbox_id,
            selectedAllocationType?.key,
            username,
            resourceKey,
            navigate,
            dispatch,
            hasDataChanged,
            showErrorAlertMessage,
        ],
    );

    // Create a single debounced save function that persists between calls
    // Uses ref to always get latest data when timer fires
    const debouncedSaveSimulation = useCallback(
        debounce(() => {
            // Debounce timer fired, saving latest data from ref
            if (latestDataRef.current) {
                saveSimulationData(latestDataRef.current);
            }
        }, 1000),
        [saveSimulationData],
    );

    /**
     * Updates simulation data field and handles auto-save logic
     *
     * @param fieldName - The field name to update in simulation data
     * @param maxPeriod - Optional max period for GL simulation time range updates
     * @returns Function that accepts the field value to update
     */
    const updateSimulationData = (fieldName: string, maxPeriod?: string) => {
        return (fieldValue: any) => {
            const configs = JSON.parse(state?.simulationData?.configs || "{}");
            if (fieldName === "timeRange") {
                const isGLSimulation = selectedAllocationType?.key === AllocationType.gl_simulation;
                if (isGLSimulation) {
                    if (maxPeriod) {
                        // For GL simulations, set both start and end period to maxPeriod
                        configs["startPeriod"] = String(maxPeriod);
                        configs["endPeriod"] = String(maxPeriod);
                        fieldValue = [maxPeriod, maxPeriod];
                    } else {
                        return;
                    }
                } else if (fieldValue.length > 0) {
                    fieldValue.sort();
                    const startPeriod = fieldValue[0] + "01";
                    configs["startPeriod"] = startPeriod;

                    const endPeriod = fieldValue[fieldValue.length - 1] + "12";
                    configs["endPeriod"] = endPeriod;

                    fieldValue = [startPeriod, endPeriod];
                }
            } else if (fieldName === InputType.allocation_definition || fieldName === InputType.allocation_mapping) {
                configs[fieldName] = fieldValue;
                // setting the system_selection field of selected input to false in case of
                // MCM exported Input selection
                if (configs["system_selection"]) {
                    configs["system_selection"][fieldName] = false;
                }
            } else {
                configs[fieldName] = fieldValue;

                // reset the time range configs in case of display period flag is true otherwise
                // set to start_period and end_period of selected scenario
                if (fieldName === "scenario") {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const scenario = scenarioList.find(({ scenario_name }) => scenario_name === fieldValue);

                    if (scenario.display_period) {
                        // reset the timeRange values
                        configs["startPeriod"] = undefined;
                        configs["endPeriod"] = undefined;
                    } else {
                        configs["startPeriod"] = scenario.start_period;
                        configs["endPeriod"] = scenario.end_period;
                    }
                    dispatch({ type: NewSimulationActionType.SET_SELECTED_SCENARIO, payload: { selectedScenario: scenario } });
                    rumService.recordBusinessEvent(
                        RUM_EVENT_TYPES.SCENARIO_SELECTED,
                        RUM_ACTIONS.SELECT_SCENARIO,
                        RUM_COMPONENTS.SIMULATION,
                        username,
                        {
                            scenario: fieldValue,
                            sandboxId: selectedSandbox.sandbox_id,
                            allocationType: selectedAllocationType.key,
                        },
                    );
                }
            }

            // Create slim update object with only necessary fields for API call
            const updatedSimulationData = {
                name: state.simulationData?.name,
                [fieldName]: fieldValue,
                configs: JSON.stringify(configs),
                resource_id: state.simulationData?.resource_id || currentSimIdRef.current,
            };

            dispatch({
                type: NewSimulationActionType.SET_SIMULATION_DATA,
                payload: {
                    simulationData: updatedSimulationData,
                },
            });

            // Trigger autosave for all fields except name, and not for GL simulations
            const isGLSimulation = selectedAllocationType?.key === AllocationType.gl_simulation;
            // Don't autosave for final states - these simulations are read-only
            const isFinalState = [SimStatus.COMPLETED, SimStatus.FAILED, SimStatus.CANCELED].includes(state.simulationData?.status);
            if (isSaveEnabled(updatedSimulationData) && !isGLSimulation && !isFinalState) {
                // Only set latestDataRef if it's our custom slim object
                const isCustomUpdateObject = !updatedSimulationData.sandbox_id;
                if (isCustomUpdateObject) {
                    latestDataRef.current = updatedSimulationData;
                }
                setIsDebouncing(true);
                // Call debounced function (reads from ref, not parameter)
                debouncedSaveSimulation();
            }
        };
    };

    const getSelectedInput = useCallback(
        (fieldName: string) => {
            const configs = JSON.parse(state.simulationData?.configs || "{}");
            return configs[fieldName] ?? "";
        },
        [state.simulationData],
    );

    /**
     * Wrapper for setActiveSimulationStepper that handles URL transitions
     * - Step 1 → Step 2+: Transitions from query to path parameter
     * - Other navigation: Updates step parameter if it exists in URL
     */
    const handleStepChange = useCallback(
        (newStep: IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>) => {
            const simIdInQuery = searchParams.get("simId");
            const currentStepParam = searchParams.get("step");
            const isMovingFromStep1 = state.simulationSteppers?.[0]?.type === state.activeSimulationStepper?.type;
            const isMovingToStep2Plus = state.simulationSteppers?.[0]?.type !== newStep.type;

            // Case 1: Moving from Step 1 to Step 2+ with simId in query → transition to path
            if (simIdInQuery && isMovingFromStep1 && isMovingToStep2Plus && selectedSandbox?.sandbox_id) {
                navigate(
                    {
                        pathname: "/simulation/" + simIdInQuery,
                        search: createSearchParams({
                            sandboxId: selectedSandbox.sandbox_id,
                            allocationType: selectedAllocationType.key,
                            step: newStep.type,
                        }).toString(),
                    },
                    { replace: true },
                );
            }
            // Case 2: Step parameter exists in URL → update it (forward or backward navigation)
            else if (currentStepParam && selectedSandbox?.sandbox_id) {
                navigate(
                    {
                        pathname: window.location.pathname,
                        search: createSearchParams({
                            sandboxId: selectedSandbox.sandbox_id,
                            allocationType: selectedAllocationType.key,
                            step: newStep.type,
                        }).toString(),
                    },
                    { replace: true },
                );
            }
            // Case 3: No step in URL → normal step change
            else {
                dispatch({
                    type: NewSimulationActionType.SET_ACTIVE_SIMULATION_STEPPER,
                    payload: { activeSimulationStepper: newStep },
                });
            }
        },
        [
            state.simulationSteppers,
            state.activeSimulationStepper,
            searchParams,
            selectedSandbox,
            selectedAllocationType,
            navigate,
            dispatch,
        ],
    );

    return (
        <NewSimulationContext.Provider
            value={{
                ...state,
                scenarioList,
                isSaving,
                isDebouncing,
                isLoadingSimulation,
                autoSaveMode: !!searchParams.get("autoSave"),
                isSimulationHeaderEnabled,
                isSaveEnabled,
                isStepperEnabled,
                isNextButtonEnabled,
                isStartRunEnabled,
                getSelectedInput,
                updateSimulationData,
                handleStepChange,
                setReadOnly: setReadOnly(dispatch),
                setSimulationId: setSimulationId(dispatch),
                setSimulationData: setSimulationData(dispatch),
                setSelectedScenario: setSelectedScenario(dispatch),
                setSimulationInputs: setSimulationInputs(dispatch),
                setSelectedCostPoolList: setSelectedCostPoolList(dispatch),
                setActiveSimulationStepper: setActiveSimulationStepper(dispatch),
                setSimulationSteppers: setSimulationSteppers(dispatch),
                setIsSystemDefinitionAndMappingSelected: setIsSystemDefinitionAndMappingSelected(dispatch),
            }}>
            {children}
        </NewSimulationContext.Provider>
    );
};
